import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Edit3, Loader2, ExternalLink, CheckCircle2, XCircle, RefreshCw, Clock, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGES = {
  pending:   { label: 'En attente', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  approved:  { label: 'Approuvée', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  rejected:  { label: 'Refusée',   cls: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  cancelled: { label: 'Annulée',   cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: X },
};

const renderProposedSummary = (changes = {}) => {
  const parts = [];
  if (changes.departure?.address) parts.push(`Départ → ${changes.departure.address}`);
  if (changes.arrival?.address) parts.push(`Arrivée → ${changes.arrival.address}`);
  if (changes.merchandiseDescription) parts.push(`Marchandise → ${changes.merchandiseDescription}`);
  if (changes.weight !== undefined && changes.weight !== null) parts.push(`Poids → ${changes.weight} t`);
  if (changes.vehicleType) parts.push(`Véhicule → ${changes.vehicleType}`);
  return parts;
};

const AdminTransferModificationRequests = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [decisionDialog, setDecisionDialog] = useState({ open: false, req: null, decision: 'approved' });
  const [adminResponse, setAdminResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/transfer-modification-requests`, {
        params: { status: statusFilter, limit: 200 }
      });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDecision = (req, decision) => {
    setDecisionDialog({ open: true, req, decision });
    setAdminResponse('');
  };

  const submitDecision = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/transfer-modification-requests/${decisionDialog.req.id}/decide`,
        { decision: decisionDialog.decision, adminResponse: adminResponse.trim() || undefined },
        {},
      );
      toast.success(decisionDialog.decision === 'approved' ? 'Demande approuvée' : 'Demande refusée');
      setDecisionDialog({ open: false, req: null, decision: 'approved' });
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la décision');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-modif-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Edit3 className="w-6 h-6 text-emerald-600" />
            Demandes de modification de transferts
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {total} demande{total > 1 ? 's' : ''} {statusFilter !== 'all' ? `· Filtre: ${STATUS_BADGES[statusFilter]?.label}` : ''}
          </p>
        </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="approved">Approuvées</SelectItem>
                <SelectItem value="rejected">Refusées</SelectItem>
                <SelectItem value="cancelled">Annulées</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchItems} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500 text-sm">
              Aucune demande {statusFilter === 'pending' ? 'en attente' : ''} pour le moment.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((req) => {
              const meta = STATUS_BADGES[req.status] || STATUS_BADGES.pending;
              const Icon = meta.icon;
              const summary = renderProposedSummary(req.proposedChanges);
              return (
                <Card key={req.id} data-testid={`admin-modif-${req.id}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-xs ${meta.cls}`}>
                          <Icon className="w-3 h-3 mr-1" /> {meta.label}
                        </Badge>
                        <span className="text-sm font-medium text-slate-700">
                          {req.clientCompanyName || req.requestedBy}
                        </span>
                        <span className="text-xs text-slate-500">
                          · {new Date(req.createdAt).toLocaleString('fr-FR')}
                        </span>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => navigate(`/admin/contracts/${req.contractId}/transfers/${req.runId}`)}
                        data-testid={`open-transfer-${req.id}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-1" />
                        Voir le transfert
                      </Button>
                    </div>

                    {summary.length > 0 && (
                      <div className="bg-slate-50 rounded-md p-3">
                        <p className="text-xs font-semibold text-slate-700 mb-1">Modifications proposées :</p>
                        <ul className="text-sm text-slate-700 space-y-0.5">
                          {summary.map((s, i) => <li key={i}>• {s}</li>)}
                        </ul>
                      </div>
                    )}

                    {req.proposedChanges?.comment && (
                      <div className="border-l-2 border-emerald-300 bg-emerald-50/40 pl-3 py-1">
                        <p className="text-xs text-slate-700 italic">"{req.proposedChanges.comment}"</p>
                      </div>
                    )}

                    {req.adminResponse && (
                      <div className="border-l-2 border-blue-300 bg-blue-50/40 pl-3 py-1">
                        <p className="text-xs font-medium text-blue-900">Réponse admin :</p>
                        <p className="text-xs text-blue-800">{req.adminResponse}</p>
                        {req.decidedBy && (
                          <p className="text-[10px] text-blue-700 mt-0.5">
                            par {req.decidedBy} · {new Date(req.decidedAt).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                    )}

                    {req.status === 'pending' && (
                      <div className="flex gap-2 pt-1 border-t">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => openDecision(req, 'approved')}
                          data-testid={`approve-${req.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approuver
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => openDecision(req, 'rejected')}
                          data-testid={`reject-${req.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Refuser
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Modal décision */}
        <Dialog open={decisionDialog.open} onOpenChange={(v) => setDecisionDialog(d => ({ ...d, open: v }))}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {decisionDialog.decision === 'approved' ? 'Approuver la demande' : 'Refuser la demande'}
              </DialogTitle>
              <DialogDescription>
                Le client recevra une notification avec votre commentaire.
                {decisionDialog.decision === 'approved' && (
                  <> N'oubliez pas d'appliquer manuellement les changements demandés sur le transfert.</>
                )}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Commentaire pour le client (optionnel mais recommandé)…"
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              rows={4}
              data-testid="admin-decision-response"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setDecisionDialog({ open: false, req: null, decision: 'approved' })} disabled={submitting}>
                Annuler
              </Button>
              <Button
                onClick={submitDecision}
                disabled={submitting}
                className={decisionDialog.decision === 'approved'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-decision"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </div>
  );
};

export default AdminTransferModificationRequests;
